import {
  useCreateCostItem,
  useDeleteCostItem,
  useUpdateCostItem
} from '@services/settlements/mutations/useCostItemMutations'
import { useCopyCostItems } from '@services/settlements/mutations/useCopyCostItems'
import type { SettlementDetail } from '@services/settlements/types'
import { CopyPlus, ListPlus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'

import { MaybeCard } from './SectionCard'
import { UnitInput } from './UnitInput'
import { money } from './format'

/**
 * The management company's annual amounts. One lump sum or an itemised
 * breakdown — both are valid; the breakdown is what makes a missing item
 * visible.
 */
export function SettlementCostItemsCard({
  settlement,
  readOnly,
  embedded = false
}: {
  settlement: SettlementDetail
  readOnly: boolean
  /** Render without the card chrome, inside a wizard step. */
  embedded?: boolean
}) {
  const { t } = useTranslation('settlement')
  const { confirm, dialog } = useConfirm()

  const create = useCreateCostItem()
  const update = useUpdateCostItem()
  const remove = useDeleteCostItem()
  const copy = useCopyCostItems()

  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')

  const share = Number(settlement.result?.proration_share ?? 0)

  const onAdd = () => {
    if (!name.trim() || !amount) return
    create.mutate(
      {
        id: settlement.id,
        body: {
          name: name.trim(),
          annual_amount: amount,
          order: settlement.cost_items.length
        }
      },
      {
        onSuccess: () => {
          setName('')
          setAmount('')
        }
      }
    )
  }

  const onDelete = async (itemId: string) => {
    if (await confirm({ title: t('cost_items.delete_confirm'), destructive: true })) {
      remove.mutate({ id: settlement.id, itemId })
    }
  }

  const action = (
    !readOnly && (
      <Button
        variant="outline"
        size="sm"
        onClick={() => copy.mutate({ id: settlement.id })}
        disabled={copy.isPending}
      >
        <CopyPlus className="mr-1.5 h-4 w-4" />
        {t('cost_items.copy_previous')}
      </Button>
    )
  )

  const body = (
    <>
      {settlement.cost_items.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          {t('cost_items.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {settlement.cost_items.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-ink/[0.08] px-3 py-2 sm:grid-cols-[1fr_160px_110px_auto_auto]"
            >
              <Input
                defaultValue={item.name}
                disabled={readOnly}
                onBlur={(event) =>
                  event.target.value !== item.name &&
                  update.mutate({
                    id: settlement.id,
                    itemId: item.id,
                    body: { name: event.target.value }
                  })
                }
              />
              <UnitInput
                unit="€"
                defaultValue={item.annual_amount}
                inputMode="decimal"
                disabled={readOnly}
                onBlur={(event) =>
                  event.target.value !== item.annual_amount &&
                  update.mutate({
                    id: settlement.id,
                    itemId: item.id,
                    body: { annual_amount: event.target.value }
                  })
                }
              />
              <span className="text-right text-sm tabular-nums text-muted-foreground">
                {money(
                  item.prorate
                    ? Number(item.annual_amount) * share
                    : Number(item.annual_amount)
                )}
              </span>
              <Switch
                checked={item.prorate}
                disabled={readOnly}
                onCheckedChange={(value) =>
                  update.mutate({
                    id: settlement.id,
                    itemId: item.id,
                    body: { prorate: value }
                  })
                }
              />
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(item.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Input
            className="min-w-40 flex-1"
            placeholder={t('cost_items.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <UnitInput
            className="w-36"
            unit="€"
            inputMode="decimal"
            placeholder={t('cost_items.annual_amount')}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <Button onClick={onAdd} disabled={create.isPending}>
            {t('cost_items.add')}
          </Button>
        </div>
      )}

    </>
  )

  return (
    <>
      <MaybeCard
        embedded={embedded}
        title={t('cost_items.title')}
        icon={ListPlus}
        action={action}
      >
        {body}
      </MaybeCard>
      {dialog}
    </>
  )
}
